import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { LogOut, Plus, Edit, Trash2, FileText, Film, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [caseStudies, setCaseStudies] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState("case-studies");

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [navigate]);

  const getAuthHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("adminToken")}`
  });

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const [casesRes, portfolioRes, contactsRes] = await Promise.all([
        axios.get(`${API}/case-studies`),
        axios.get(`${API}/portfolio`),
        axios.get(`${API}/contact`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setCaseStudies(casesRes.data);
      setPortfolio(portfolioRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem("adminToken");
        navigate("/admin/login");
        return;
      }
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    toast.success("Logged out successfully");
    navigate("/admin/login");
  };

  const handleDeleteCaseStudy = async (id) => {
    if (!window.confirm("Are you sure you want to delete this case study?")) return;
    try {
      await axios.delete(`${API}/case-studies/${id}`, { headers: getAuthHeader() });
      toast.success("Case study deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete case study");
    }
  };

  const handleDeletePortfolio = async (id) => {
    if (!window.confirm("Are you sure you want to delete this portfolio item?")) return;
    try {
      await axios.delete(`${API}/portfolio/${id}`, { headers: getAuthHeader() });
      toast.success("Portfolio item deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete portfolio item");
    }
  };

  const handleSaveCaseStudy = async (data) => {
    try {
      if (editingItem?.id) {
        await axios.put(`${API}/case-studies/${editingItem.id}`, data, { headers: getAuthHeader() });
        toast.success("Case study updated");
      } else {
        await axios.post(`${API}/case-studies`, data, { headers: getAuthHeader() });
        toast.success("Case study created");
      }
      setIsDialogOpen(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.slice(-1)[0]}: ${d.msg}`).join("; ")
        : detail || error.message;
      toast.error(`Failed to save case study — ${msg}`);
    }
  };

  const handleSavePortfolio = async (data) => {
    try {
      if (editingItem?.id) {
        await axios.put(`${API}/portfolio/${editingItem.id}`, data, { headers: getAuthHeader() });
        toast.success("Portfolio item updated");
      } else {
        await axios.post(`${API}/portfolio`, data, { headers: getAuthHeader() });
        toast.success("Portfolio item created");
      }
      setIsDialogOpen(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.slice(-1)[0]}: ${d.msg}`).join("; ")
        : detail || error.message;
      toast.error(`Failed to save portfolio item — ${msg}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      {/* Header */}
      <div className="bg-[#0A0A0A] border-b border-[#262626] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <h1 className="heading-font text-3xl" data-testid="admin-dashboard-heading">ADMIN DASHBOARD</h1>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-[#262626] text-white hover:bg-[#262626]"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="case-studies" onValueChange={setCurrentSection}>
          <TabsList className="bg-[#0A0A0A] border border-[#262626] mb-8">
            <TabsTrigger value="case-studies" className="data-[state=active]:bg-[#1cb5ae]" data-testid="tab-case-studies">
              <FileText className="w-4 h-4 mr-2" />
              Case Studies
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="data-[state=active]:bg-[#F97316]" data-testid="tab-portfolio">
              <Film className="w-4 h-4 mr-2" />
              Portfolio
            </TabsTrigger>
            <TabsTrigger value="contacts" className="data-[state=active]:bg-[#8B5CF6]" data-testid="tab-contacts">
              <Mail className="w-4 h-4 mr-2" />
              Contacts
            </TabsTrigger>
          </TabsList>

          {/* Case Studies Tab */}
          <TabsContent value="case-studies">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold">Case Studies</h2>
              <Dialog open={isDialogOpen && currentSection === "case-studies"} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1cb5ae] hover:bg-[#1aada5]" onClick={() => setEditingItem(null)} data-testid="add-case-study-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Case Study
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0A0A0A] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingItem ? "Edit" : "Add"} Case Study</DialogTitle>
                  </DialogHeader>
                  <CaseStudyForm onSave={handleSaveCaseStudy} initialData={editingItem} />
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="text-center py-20">Loading...</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {caseStudies.map((study) => (
                  <div key={study.id} className="glass-effect rounded-lg p-6" data-testid={`case-study-item-${study.id}`}>
                    <h3 className="text-xl font-semibold mb-2">{study.title}</h3>
                    <p className="text-gray-400 mb-4">{study.client}</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingItem(study);
                          setIsDialogOpen(true);
                        }}
                        data-testid={`edit-case-study-${study.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteCaseStudy(study.id)}
                        className="text-red-400 hover:text-red-300"
                        data-testid={`delete-case-study-${study.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold">Portfolio</h2>
              <Dialog open={isDialogOpen && currentSection === "portfolio"} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#F97316] hover:bg-[#EA580C]" onClick={() => setEditingItem(null)} data-testid="add-portfolio-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Portfolio Item
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0A0A0A] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingItem ? "Edit" : "Add"} Portfolio Item</DialogTitle>
                  </DialogHeader>
                  <PortfolioForm onSave={handleSavePortfolio} initialData={editingItem} />
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="text-center py-20">Loading...</div>
            ) : (
              <div className="grid md:grid-cols-3 gap-6">
                {portfolio.map((item) => (
                  <div key={item.id} className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-6" data-testid={`portfolio-item-${item.id}`}>
                    <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                    <p className="text-gray-400 mb-4">{item.client}</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingItem(item);
                          setIsDialogOpen(true);
                        }}
                        data-testid={`edit-portfolio-${item.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeletePortfolio(item.id)}
                        className="text-red-400 hover:text-red-300"
                        data-testid={`delete-portfolio-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <h2 className="text-2xl font-semibold mb-6">Contact Form Submissions</h2>
            {loading ? (
              <div className="text-center py-20">Loading...</div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-20 text-gray-400">No contact submissions yet</div>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <div key={contact.id} className="glass-effect rounded-lg p-6" data-testid={`contact-item-${contact.id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-semibold">{contact.name}</h3>
                        <p className="text-gray-400">{contact.email}</p>
                      </div>
                      <span className="text-sm text-gray-500">
                        {new Date(contact.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {contact.company && (
                      <p className="text-gray-400 mb-2">Company: {contact.company}</p>
                    )}
                    <p className="text-sm text-gray-500 mb-2">Service: {contact.service_type}</p>
                    <p className="text-gray-300">{contact.message}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Case Study Form Component
const CaseStudyForm = ({ onSave, initialData }) => {
  const [formData, setFormData] = useState(
    initialData || {
      title: "",
      client: "",
      industry: "",
      challenge: "",
      strategy: "",
      execution: "",
      tools: [],
      results: "",
      video_url: "",
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const toolsArray = Array.isArray(formData.tools)
      ? formData.tools
      : String(formData.tools || "")
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
    const data = { ...formData, tools: toolsArray };
    onSave(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="case-study-form">
      <div>
        <Label>Title *</Label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
          className="bg-[#030303] border-[#262626]"
        />
      </div>
      <div>
        <Label>Client *</Label>
        <Input
          value={formData.client}
          onChange={(e) => setFormData({ ...formData, client: e.target.value })}
          required
          className="bg-[#030303] border-[#262626]"
        />
      </div>
      <div>
        <Label>Industry *</Label>
        <Input
          value={formData.industry}
          onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
          required
          className="bg-[#030303] border-[#262626]"
        />
      </div>
      <div>
        <Label>Challenge *</Label>
        <Textarea
          value={formData.challenge}
          onChange={(e) => setFormData({ ...formData, challenge: e.target.value })}
          required
          className="bg-[#030303] border-[#262626]"
        />
      </div>
      <div>
        <Label>Strategy *</Label>
        <Textarea
          value={formData.strategy}
          onChange={(e) => setFormData({ ...formData, strategy: e.target.value })}
          required
          className="bg-[#030303] border-[#262626]"
        />
      </div>
      <div>
        <Label>Execution *</Label>
        <Textarea
          value={formData.execution}
          onChange={(e) => setFormData({ ...formData, execution: e.target.value })}
          required
          className="bg-[#030303] border-[#262626]"
        />
      </div>
      <div>
        <Label>Tools (comma-separated) *</Label>
        <Input
          value={Array.isArray(formData.tools) ? formData.tools.join(", ") : formData.tools}
          onChange={(e) => setFormData({ ...formData, tools: e.target.value })}
          required
          placeholder="e.g., Google Ads, Meta Ads, AI Tools"
          className="bg-[#030303] border-[#262626]"
        />
      </div>
      <div>
        <Label>Results *</Label>
        <Textarea
          value={formData.results}
          onChange={(e) => setFormData({ ...formData, results: e.target.value })}
          required
          className="bg-[#030303] border-[#262626]"
        />
      </div>
      <Button type="submit" className="w-full bg-[#1cb5ae] hover:bg-[#1aada5]" data-testid="save-case-study-btn">
        Save Case Study
      </Button>
    </form>
  );
};

// Portfolio Form Component
const PortfolioForm = ({ onSave, initialData }) => {
  const [formData, setFormData] = useState(
    initialData || {
      title: "",
      client: "",
      project_type: "Commercials",
      description: "",
      image_url: "",
      video_url: "",
      thumbnail_url: ""
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="portfolio-form">
      <div>
        <Label>Title *</Label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
          className="bg-[#030303] border-[#262626]"
        />
      </div>
      <div>
        <Label>Client *</Label>
        <Input
          value={formData.client}
          onChange={(e) => setFormData({ ...formData, client: e.target.value })}
          required
          className="bg-[#030303] border-[#262626]"
        />
      </div>
      <div>
        <Label>Project Type *</Label>
        <select
          value={formData.project_type}
          onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
          required
          className="w-full px-3 py-2 bg-[#030303] border border-[#262626] text-white rounded-md"
        >
          <option value="Commercials">Commercials</option>
          <option value="Brand Films">Brand Films</option>
          <option value="Social Content">Social Content</option>
          <option value="Motion">Motion & Animation</option>
          <option value="BTS">BTS / Studio Work</option>
        </select>
      </div>
      <div>
        <Label>Description *</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          required
          className="bg-[#030303] border-[#262626]"
        />
      </div>
      <div>
        <Label>Video URL (YouTube/Vimeo)</Label>
        <Input
          value={formData.video_url}
          onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
          placeholder="https://youtube.com/watch?v=..."
          className="bg-[#030303] border-[#262626]"
        />
      </div>
      <div>
        <Label>Thumbnail URL</Label>
        <Input
          value={formData.thumbnail_url}
          onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
          className="bg-[#030303] border-[#262626]"
        />
      </div>
      <div>
        <Label>Image URL</Label>
        <Input
          value={formData.image_url}
          onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
          className="bg-[#030303] border-[#262626]"
        />
      </div>
      <Button type="submit" className="w-full bg-[#F97316] hover:bg-[#EA580C]" data-testid="save-portfolio-btn">
        Save Portfolio Item
      </Button>
    </form>
  );
};

export default AdminDashboard;
